/**
 * WeeklyEnergyChart Component
 *
 * 7-day combo chart — same pattern as the day view but across a full week.
 * X-axis shows day labels with 6-hour major ticks.
 * Midnight boundaries marked with reference lines.
 */

import { useMemo } from 'react'
import { Skeleton } from '@/components/ui/skeleton'
import {
  ComposedChart,
  Bar,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  ResponsiveContainer
} from 'recharts'
import { useWeeklyEnergy } from '@/hooks/useWeeklyEnergy'

const FIXED_COLOR = '#f59e0b'
const AGILE_COLOR = '#0ea5e9'
const BAR_COLOR = '#a3a3a3'
const SAVING_COLOR = '#22c55e'
const DEFICIT_COLOR = '#ef4444'

function WeekTooltip({ active, payload }) {
  if (!active || !payload?.length) return null
  const row = payload[0]?.payload
  if (!row) return null

  return (
    <div className="bg-white border border-neutral-200 rounded-lg shadow-lg p-3 min-w-48">
      <p className="font-medium text-neutral-900 mb-2">{row.day_label} {row.slot_label}</p>

      {row.consumption_kwh != null && (
        <div className="flex items-center justify-between gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: BAR_COLOR }} />
            <span className="text-neutral-600">Usage</span>
          </div>
          <span className="font-medium text-neutral-900">
            {parseFloat(row.consumption_kwh).toFixed(2)} kWh
          </span>
        </div>
      )}

      <div className="flex items-center justify-between gap-4 text-sm mt-1">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: FIXED_COLOR }} />
          <span className="text-neutral-600">Fixed</span>
        </div>
        <span className="font-medium text-neutral-900">
          {row.fixedRate != null ? `${row.fixedRate.toFixed(1)}p` : '—'}
        </span>
      </div>

      {row.agileRate != null && (
        <div className="flex items-center justify-between gap-4 text-sm mt-1">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: AGILE_COLOR }} />
            <span className="text-neutral-600">Agile</span>
          </div>
          <span className="font-medium text-neutral-900">
            {row.agileRate.toFixed(1)}p
          </span>
        </div>
      )}

      {row.agileRate != null && row.fixedRate != null && (
        <div className="border-t border-neutral-200 mt-2 pt-2 text-sm">
          {(() => {
            const delta = row.fixedRate - row.agileRate
            return (
              <span className={delta >= 0 ? 'text-green-600' : 'text-red-600'}>
                {delta >= 0
                  ? `${delta.toFixed(1)}p cheaper on Agile`
                  : `${Math.abs(delta).toFixed(1)}p dearer on Agile`
                }
              </span>
            )
          })()}
        </div>
      )}
    </div>
  )
}

export default function WeeklyEnergyChart({ weekStart, refreshKey }) {
  const startStr = useMemo(() => {
    const y = weekStart.getFullYear()
    const m = String(weekStart.getMonth() + 1).padStart(2, '0')
    const d = String(weekStart.getDate()).padStart(2, '0')
    return `${y}-${m}-${d}`
  }, [weekStart])

  const { data, isLoading } = useWeeklyEnergy(startStr, refreshKey)

  const chartData = useMemo(() => {
    if (!data?.length) return []
    return data.map((row, i) => {
      const fixed = row.fixed_rate_pence != null ? parseFloat(row.fixed_rate_pence) : null
      const agile = row.agile_rate_pence != null ? parseFloat(row.agile_rate_pence) : null

      return {
        ...row,
        index: i,
        consumption_kwh: row.consumption_kwh != null ? parseFloat(row.consumption_kwh) : null,
        fixedRate: fixed,
        agileRate: agile,
        savingBand: agile != null && fixed != null && agile < fixed ? [agile, fixed] : null,
        deficitBand: agile != null && fixed != null && agile > fixed ? [fixed, agile] : null,
        negativeBand: agile != null ? (agile < 0 ? [agile, 0] : [0, 0]) : null,
      }
    })
  }, [data])

  const priceDomain = useMemo(() => {
    if (!chartData.length) return [0, 40]
    const prices = chartData
      .flatMap(r => [r.fixedRate, r.agileRate])
      .filter(v => v != null)
    if (!prices.length) return [0, 40]
    const min = Math.min(...prices)
    const max = Math.max(...prices)
    return [Math.floor(Math.min(min - 2, 0)), Math.ceil(max + 2)]
  }, [chartData])

  const hasNegativeRates = chartData.some(r => r.agileRate != null && r.agileRate < 0)

  // X-axis ticks: day label centred at 12:00 in each day
  const xTicks = useMemo(() => {
    if (!chartData.length) return []
    return chartData
      .filter(r => r.slot_label === '12:00')
      .map(r => r.index)
  }, [chartData])

  // Midnight indices for vertical reference lines (day boundaries)
  const midnightIndices = useMemo(() => {
    if (!chartData.length) return []
    // Skip first midnight (index 0) — it's the start of the chart
    return chartData
      .filter((r, i) => i > 0 && r.slot_label === '00:00')
      .map(r => r.index)
  }, [chartData])

  // Summary stats
  const summary = useMemo(() => {
    const slotsWithUsage = chartData.filter(r => r.consumption_kwh != null)
    if (!slotsWithUsage.length) return null

    const totalKwh = slotsWithUsage.reduce((sum, r) => sum + r.consumption_kwh, 0)
    const fixedCostPence = slotsWithUsage.reduce(
      (sum, r) => sum + r.consumption_kwh * (r.fixedRate ?? 0), 0
    )
    const slotsWithAgile = slotsWithUsage.filter(r => r.agileRate != null)
    const agileCostPence = slotsWithAgile.reduce(
      (sum, r) => sum + r.consumption_kwh * r.agileRate, 0
    )

    return {
      totalKwh,
      fixedCost: fixedCostPence / 100,
      agileCost: slotsWithAgile.length > 0 ? agileCostPence / 100 : null,
    }
  }, [chartData])

  // Show day name at each tick (centred in the day)
  const formatTick = (index) => {
    const row = chartData[index]
    if (!row) return ''
    return row.day_label
  }

  if (isLoading) {
    return <Skeleton className="h-[280px] w-full" />
  }

  if (!chartData.length) {
    return (
      <div className="flex items-center justify-center h-[280px] text-neutral-500">
        No data available for this week
      </div>
    )
  }

  return (
    <>
      {/* Summary stats */}
      <div className="flex flex-wrap gap-x-6 gap-y-1 mb-4 text-sm">
        {summary ? (
          <>
            <div>
              <span className="text-muted-foreground">Usage:</span>{' '}
              <span className="font-semibold">{summary.totalKwh.toFixed(1)} kWh</span>
            </div>
            <div title={`£${summary.fixedCost.toFixed(2)} excl VAT / £${(summary.fixedCost * 1.2).toFixed(2)} inc VAT`}>
              <span className="text-muted-foreground">Fixed:</span>{' '}
              <span className="font-semibold" style={{ color: FIXED_COLOR }}>
                £{summary.fixedCost.toFixed(2)}
              </span>
            </div>
            {summary.agileCost != null && (
              <>
                <div title={`£${summary.agileCost.toFixed(2)} excl VAT / £${(summary.agileCost * 1.2).toFixed(2)} inc VAT`}>
                  <span className="text-muted-foreground">Agile:</span>{' '}
                  <span className="font-semibold" style={{ color: AGILE_COLOR }}>
                    £{summary.agileCost.toFixed(2)}
                  </span>
                </div>
                <span className="text-border">|</span>
                <div title={`£${Math.abs(summary.fixedCost - summary.agileCost).toFixed(2)} excl VAT / £${(Math.abs(summary.fixedCost - summary.agileCost) * 1.2).toFixed(2)} inc VAT`}>
                  <span className="text-muted-foreground">Saving:</span>{' '}
                  <span className={`font-semibold ${summary.agileCost <= summary.fixedCost ? 'text-green-600' : 'text-red-600'}`}>
                    {summary.agileCost <= summary.fixedCost
                      ? `£${(summary.fixedCost - summary.agileCost).toFixed(2)}`
                      : `-£${(summary.agileCost - summary.fixedCost).toFixed(2)}`
                    }
                  </span>
                </div>
              </>
            )}
          </>
        ) : (
          <span className="text-muted-foreground">Prices only — no consumption data for this week</span>
        )}
      </div>

      <ResponsiveContainer width="100%" height={280}>
        <ComposedChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />

          <XAxis
            dataKey="index"
            tick={{ fontSize: 11, fill: '#6b7280' }}
            axisLine={false}
            tickLine={false}
            ticks={xTicks}
            tickFormatter={formatTick}
            interval={0}
          />

          <YAxis
            yAxisId="kwh"
            orientation="left"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 11, fill: '#6b7280' }}
            tickFormatter={(v) => `${v}`}
            label={{
              value: 'kWh',
              angle: -90,
              position: 'insideLeft',
              offset: 10,
              style: { fontSize: 11, fill: '#9ca3af' }
            }}
          />

          <YAxis
            yAxisId="price"
            orientation="right"
            domain={priceDomain}
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 11, fill: '#6b7280' }}
            tickFormatter={(v) => `${v}p`}
          />

          <Tooltip content={<WeekTooltip />} />

          <Legend
            wrapperStyle={{ fontSize: '12px', paddingTop: '8px' }}
            iconType="line"
          />

          {/* Day boundary lines */}
          {midnightIndices.map(idx => (
            <ReferenceLine
              key={`midnight-${idx}`}
              yAxisId="price"
              x={idx}
              stroke="#d4d4d4"
              strokeWidth={1}
              strokeDasharray="3 3"
            />
          ))}

          {hasNegativeRates && (
            <ReferenceLine
              yAxisId="price"
              y={0}
              stroke="#374151"
              strokeWidth={1.5}
              strokeDasharray="4 2"
            />
          )}

          <Area yAxisId="price" type="stepAfter" dataKey="savingBand" stroke="none" fill={SAVING_COLOR} fillOpacity={0.18} isAnimationActive={false} activeDot={false} legendType="none" connectNulls={false} />
          <Area yAxisId="price" type="stepAfter" dataKey="deficitBand" stroke="none" fill={DEFICIT_COLOR} fillOpacity={0.18} isAnimationActive={false} activeDot={false} legendType="none" connectNulls={false} />
          <Area yAxisId="price" type="stepAfter" dataKey="negativeBand" stroke="none" fill={SAVING_COLOR} fillOpacity={0.4} isAnimationActive={false} activeDot={false} legendType="none" connectNulls={false} />

          <Bar yAxisId="kwh" dataKey="consumption_kwh" name="Usage (kWh)" fill={BAR_COLOR} fillOpacity={0.5} radius={[1, 1, 0, 0]} isAnimationActive={false} legendType="rect" />

          <Line yAxisId="price" type="stepAfter" dataKey="fixedRate" name="Fixed" stroke={FIXED_COLOR} strokeWidth={2} dot={false} isAnimationActive={false} />
          <Line yAxisId="price" type="stepAfter" dataKey="agileRate" name="Agile" stroke={AGILE_COLOR} strokeWidth={1.5} dot={false} isAnimationActive={false} />
        </ComposedChart>
      </ResponsiveContainer>
    </>
  )
}
